import { Chromosome } from '../types/algorithm';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';
import { calculateChromosomeFitness, calculateEnergy } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  tournamentSelection,
  singlePointCrossover,
  findBestChromosome,
} from './geneticAlgorithm';

/**
 * SFLACA (Shuffled Frog Leaping Algorithm + Cultural Algorithm) implementation
 * Combines SFLA memeplex structure with CA belief space (situational, normative, constraint knowledge)
 * Includes GA-style diversity injection and adaptive control
 */

/**
 * Belief Space structure
 */
interface BeliefSpace {
  // Situational knowledge: best exemplars
  bestFitnessFrog: Chromosome | null;
  bestEnergyFrog: Chromosome | null;
  
  // Normative knowledge: preferred sets per task
  preferredSets: number[][]; // preferredSets[task][0..M-1] = top M serviceIds
  probabilities: number[][]; // probabilities[task][serviceId] = normalized frequency
  
  // Constraint knowledge: forbidden sets per task (optional)
  forbiddenSets: Set<number>[]; // forbiddenSets[task] = set of serviceIds to avoid
  
  // Adaptive parameters
  beta: number; // Belief influence probability (adaptive)
}

/**
 * Divides population into memeplexes
 */
function createMemeplexes(
  population: Chromosome[],
  memeplexCount: number
): Chromosome[][] {
  const memeplexes: Chromosome[][] = Array(memeplexCount)
    .fill(null)
    .map(() => []);

  for (let i = 0; i < population.length; i++) {
    memeplexes[i % memeplexCount].push(population[i]);
  }

  return memeplexes;
}

/**
 * Shuffles memeplexes back into population using interleaving (classic SFLA approach)
 * Interleaves frogs from different memeplexes to maintain mixing effect
 */
function shuffleMemeplexes(
  memeplexes: Chromosome[][],
  population: Chromosome[]
): void {
  const memeplexCount = memeplexes.length;
  
  // Interleave frogs from memeplexes: for position i, use memeplex (i % memeplexCount)
  // and frog at position floor(i / memeplexCount) within that memeplex
  for (let i = 0; i < population.length; i++) {
    const memeplexIdx = i % memeplexCount;
    const frogIdx = Math.floor(i / memeplexCount);
    
    if (frogIdx < memeplexes[memeplexIdx].length) {
      population[i] = memeplexes[memeplexIdx][frogIdx];
    }
  }
}

/**
 * Repairs chromosome to ensure feasibility (valid service indices)
 */
function repairChromosome(
  chromosome: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  rng: SeededRNG
): Chromosome {
  const repaired: Chromosome = {
    x: [...chromosome.x],
    fitness: 0,
    rwp: 0,
  };

  // Fix any out-of-range genes
  for (let i = 0; i < taskCount; i++) {
    if (repaired.x[i] < 0 || repaired.x[i] >= serviceCount) {
      repaired.x[i] = rng.randomInt(serviceCount);
    }
  }

  calculateChromosomeFitness(repaired, matrix, taskCount, serviceCount);
  return repaired;
}

/**
 * Calculates Hamming similarity between two chromosomes
 */
function calculateHammingSimilarity(chrom1: Chromosome, chrom2: Chromosome, taskCount: number): number {
  let sameGenes = 0;
  for (let i = 0; i < taskCount; i++) {
    if (chrom1.x[i] === chrom2.x[i]) {
      sameGenes++;
    }
  }
  return sameGenes / taskCount;
}

/**
 * Calculates average Hamming similarity in population (diversity measure)
 */
function calculatePopulationDiversity(population: Chromosome[], taskCount: number): number {
  if (population.length < 2) return 0;
  
  let totalSimilarity = 0;
  let comparisons = 0;
  
  for (let i = 0; i < population.length; i++) {
    for (let j = i + 1; j < population.length; j++) {
      totalSimilarity += calculateHammingSimilarity(population[i], population[j], taskCount);
      comparisons++;
    }
  }
  
  return comparisons > 0 ? totalSimilarity / comparisons : 0;
}

/**
 * Multi-objective comparison: returns true if chrom1 is better than chrom2
 * Priority: fitness > energy (lower is better for energy)
 */
function isBetterThan(chrom1: Chromosome, chrom2: Chromosome, matrix: number[][][], taskCount: number, serviceCount: number): boolean {
  const energy1 = calculateEnergy(chrom1.x, matrix, taskCount, serviceCount);
  const energy2 = calculateEnergy(chrom2.x, matrix, taskCount, serviceCount);
  
  // Primary: fitness (higher is better)
  if (chrom1.fitness > chrom2.fitness) return true;
  if (chrom1.fitness < chrom2.fitness) return false;
  
  // Secondary: energy (lower is better)
  return energy1 < energy2;
}

/**
 * Cultural Acceptance: Updates belief space from accepted set
 */
function updateBeliefSpace(
  beliefSpace: BeliefSpace,
  acceptedSet: Chromosome[],
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  M: number = 5 // Number of preferred services per task
): void {
  if (acceptedSet.length === 0) return;

  // Update situational knowledge (best exemplars)
  let bestFitness = -Infinity;
  let bestEnergy = Infinity;
  
  for (const frog of acceptedSet) {
    const energy = calculateEnergy(frog.x, matrix, taskCount, serviceCount);
    
    if (frog.fitness > bestFitness) {
      bestFitness = frog.fitness;
      if (!beliefSpace.bestFitnessFrog || frog.fitness > beliefSpace.bestFitnessFrog.fitness) {
        beliefSpace.bestFitnessFrog = { x: [...frog.x], fitness: frog.fitness, rwp: frog.rwp };
      }
    }
    
    if (energy < bestEnergy) {
      bestEnergy = energy;
      if (!beliefSpace.bestEnergyFrog || energy < calculateEnergy(beliefSpace.bestEnergyFrog.x, matrix, taskCount, serviceCount)) {
        beliefSpace.bestEnergyFrog = { x: [...frog.x], fitness: frog.fitness, rwp: frog.rwp };
      }
    }
  }

  // Update normative knowledge: compute frequencies per task
  const frequencies: number[][] = Array(taskCount)
    .fill(null)
    .map(() => new Array(serviceCount).fill(0));

  for (const frog of acceptedSet) {
    for (let task = 0; task < taskCount; task++) {
      const serviceId = frog.x[task];
      if (serviceId >= 0 && serviceId < serviceCount) {
        frequencies[task][serviceId]++;
      }
    }
  }

  // Build preferred sets and probabilities with smoothing
  const smoothingFactor = 0.1; // Add small constant for smoothing
  beliefSpace.preferredSets = [];
  beliefSpace.probabilities = [];

  for (let task = 0; task < taskCount; task++) {
    // Create array of {serviceId, frequency} pairs
    const serviceFreqs: Array<{ serviceId: number; freq: number }> = [];
    for (let serviceId = 0; serviceId < serviceCount; serviceId++) {
      serviceFreqs.push({
        serviceId,
        freq: frequencies[task][serviceId] + smoothingFactor,
      });
    }

    // Sort by frequency (descending)
    serviceFreqs.sort((a, b) => b.freq - a.freq);

    // Top M preferred services
    const preferredSet = serviceFreqs.slice(0, Math.min(M, serviceCount)).map(item => item.serviceId);
    beliefSpace.preferredSets.push(preferredSet);

    // Normalized probabilities
    const totalFreq = serviceFreqs.reduce((sum, item) => sum + item.freq, 0);
    const probabilities = new Array(serviceCount).fill(0);
    for (const item of serviceFreqs) {
      probabilities[item.serviceId] = item.freq / totalFreq;
    }
    beliefSpace.probabilities.push(probabilities);
  }

  // Initialize forbidden sets (empty for now - can be extended for constraint handling)
  if (beliefSpace.forbiddenSets.length === 0) {
    beliefSpace.forbiddenSets = Array(taskCount).fill(null).map(() => new Set<number>());
  }
}

/**
 * Applies belief influence to a gene (task)
 */
function applyBeliefInfluence(
  task: number,
  currentService: number,
  beliefSpace: BeliefSpace,
  serviceCount: number,
  beta: number, // Belief influence probability
  gamma: number, // Situational exemplar probability
  rng: SeededRNG
): number {
  // With probability gamma, use situational exemplar
  if (rng.randomChance(gamma) && beliefSpace.bestFitnessFrog) {
    return beliefSpace.bestFitnessFrog.x[task];
  }

  // With probability beta, use normative knowledge
  if (rng.randomChance(beta) && beliefSpace.preferredSets[task] && beliefSpace.preferredSets[task].length > 0) {
    const preferredSet = beliefSpace.preferredSets[task];
    const forbiddenSet = beliefSpace.forbiddenSets[task];
    
    // Filter out forbidden services
    const allowedPreferred = preferredSet.filter(s => !forbiddenSet.has(s));
    
    if (allowedPreferred.length > 0) {
      // Sample from preferred set using probabilities if available
      if (beliefSpace.probabilities[task]) {
        const r = rng.random();
        let cumProb = 0;
        for (const serviceId of allowedPreferred) {
          cumProb += beliefSpace.probabilities[task][serviceId];
          if (r <= cumProb) {
            return serviceId;
          }
        }
        // Fallback: return first preferred if probability sampling fails
        return allowedPreferred[0];
      } else {
        // Uniform selection from preferred set
        return allowedPreferred[rng.randomInt(allowedPreferred.length)];
      }
    }
  }

  // Otherwise, keep current service
  return currentService;
}

/**
 * SFLA Memeplex Evolution with belief influence
 */
function evolveMemeplexWithBelief(
  memeplex: Chromosome[],
  globalBestFrog: Chromosome,
  beliefSpace: BeliefSpace,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  localSteps: number,
  alpha: number, // Probability to copy from best frog
  beta: number, // Belief influence probability
  gamma: number, // Situational exemplar probability
  r: number, // Random reinitialization probability
  rng: SeededRNG
): void {
  if (memeplex.length < 2) return;

  // Sort memeplex (best to worst)
  memeplex.sort((a, b) => b.fitness - a.fitness);
  
  const memeplexBest = memeplex[0];
  const memeplexWorst = memeplex[memeplex.length - 1];

  // Local search steps
  for (let step = 0; step < localSteps; step++) {
    // Generate candidate by moving worst toward best
    const candidate: Chromosome = {
      x: [...memeplexWorst.x],
      fitness: 0,
      rwp: 0,
    };

    // Copy genes from best with probability alpha
    for (let i = 0; i < taskCount; i++) {
      if (rng.randomChance(alpha)) {
        candidate.x[i] = memeplexBest.x[i];
      }
      
      // Apply belief influence
      candidate.x[i] = applyBeliefInfluence(
        i,
        candidate.x[i],
        beliefSpace,
        serviceCount,
        beta,
        gamma,
        rng
      );
    }

    // Repair and evaluate
    const repaired = repairChromosome(candidate, taskCount, serviceCount, matrix, rng);
    
    // Multi-objective comparison
    if (isBetterThan(repaired, memeplexWorst, matrix, taskCount, serviceCount)) {
      memeplex[memeplex.length - 1] = repaired;
      memeplex.sort((a, b) => b.fitness - a.fitness);
      continue;
    }

    // Try moving toward global best
    const candidateGlobal: Chromosome = {
      x: [...memeplexWorst.x],
      fitness: 0,
      rwp: 0,
    };

    for (let i = 0; i < taskCount; i++) {
      if (rng.randomChance(alpha)) {
        candidateGlobal.x[i] = globalBestFrog.x[i];
      }
      candidateGlobal.x[i] = applyBeliefInfluence(
        i,
        candidateGlobal.x[i],
        beliefSpace,
        serviceCount,
        beta,
        gamma,
        rng
      );
    }

    const repairedGlobal = repairChromosome(candidateGlobal, taskCount, serviceCount, matrix, rng);
    
    if (isBetterThan(repairedGlobal, memeplexWorst, matrix, taskCount, serviceCount)) {
      memeplex[memeplex.length - 1] = repairedGlobal;
      memeplex.sort((a, b) => b.fitness - a.fitness);
      continue;
    }

    // Random reinitialization with probability r
    if (rng.randomChance(r)) {
      const randomFrog: Chromosome = {
        x: Array(taskCount).fill(0).map(() => rng.randomInt(serviceCount)),
        fitness: 0,
        rwp: 0,
      };
      calculateChromosomeFitness(randomFrog, matrix, taskCount, serviceCount);
      memeplex[memeplex.length - 1] = randomFrog;
      memeplex.sort((a, b) => b.fitness - a.fitness);
    }
  }
}

/**
 * GA-style diversity injection with belief-guided mutation
 */
function applyGADiversityInjection(
  population: Chromosome[],
  beliefSpace: BeliefSpace,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  crossoverRate: number,
  mutationRate: number,
  rng: SeededRNG,
  tournamentSize: number = 3,
  pmBelief: number = 0.7 // Probability to use belief-guided mutation
): Chromosome[] {
  const children: Chromosome[] = [];
  const numPairs = Math.floor(population.length / 2);

  // Tournament selection and crossover
  for (let i = 0; i < numPairs; i++) {
    if (rng.randomChance(crossoverRate / 100)) {
      const parent1Idx = tournamentSelection(population, rng, tournamentSize);
      const parent2Idx = tournamentSelection(population, rng, tournamentSize);
      
      const [child1, child2] = singlePointCrossover(
        population[parent1Idx],
        population[parent2Idx],
        taskCount,
        rng
      );

      children.push(child1, child2);
    }
  }

  // Belief-guided mutation
  const mutateCount = Math.floor((population.length * mutationRate) / 100);
  for (let i = 0; i < mutateCount && i < population.length; i++) {
    const idx = rng.randomInt(population.length);
    const mutated: Chromosome = {
      x: [...population[idx].x],
      fitness: 0,
      rwp: 0,
    };

    // Mutate 1-3 random genes
    const mutationCount = rng.randomIntRange(1, 3);
    const mutatedIndices = new Set<number>();
    
    for (let j = 0; j < mutationCount; j++) {
      let taskIdx: number;
      do {
        taskIdx = rng.randomInt(taskCount);
      } while (mutatedIndices.has(taskIdx));
      mutatedIndices.add(taskIdx);

      // Belief-guided mutation
      if (rng.randomChance(pmBelief) && beliefSpace.preferredSets[taskIdx] && beliefSpace.preferredSets[taskIdx].length > 0) {
        const preferredSet = beliefSpace.preferredSets[taskIdx];
        const forbiddenSet = beliefSpace.forbiddenSets[taskIdx];
        const allowedPreferred = preferredSet.filter(s => !forbiddenSet.has(s));
        
        if (allowedPreferred.length > 0) {
          mutated.x[taskIdx] = allowedPreferred[rng.randomInt(allowedPreferred.length)];
        } else {
          mutated.x[taskIdx] = rng.randomInt(serviceCount);
        }
      } else {
        // Random mutation
        mutated.x[taskIdx] = rng.randomInt(serviceCount);
      }
    }

    const repaired = repairChromosome(mutated, taskCount, serviceCount, matrix, rng);
    children.push(repaired);
  }

  return children;
}

/**
 * Runs SFLACA for specified generations
 */
export async function runSFLACA(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    memeplexCount: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void,
  seed?: number
): Promise<Chromosome> {
  // Create seeded RNG for reproducibility
  const rng = createSeededRNG(seed);
  
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount,
    rng
  );

  // Evaluate initial population (fitness and energy)
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Initialize belief space
  const beliefSpace: BeliefSpace = {
    bestFitnessFrog: null,
    bestEnergyFrog: null,
    preferredSets: [],
    probabilities: [],
    forbiddenSets: [],
    beta: 0.3, // Initial belief influence probability
  };

  // Algorithm parameters
  const p = 0.1; // Top p% for accepted set (10%)
  const M = Math.min(5, Math.max(3, Math.floor(serviceCount * 0.1))); // Preferred set size (3-8)
  const localSteps = 3; // Local search steps per memeplex
  const alpha = 0.5; // Probability to copy from best frog
  const gamma = 0.1; // Situational exemplar probability
  const r = 0.1; // Random reinitialization probability
  const S = 10; // Stagnation threshold (generations)
  const elitePercent = 0.02; // Elitism (1-3%)

  // Adaptive tracking
  let bestFitnessHistory: number[] = [];
  let stagnationCount = 0;

  // Evolution loop
  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100; // Update UI every 100ms (PERFORMANCE OPTIMIZATION)
  
  for (let gen = 0; gen < parameters.generations; gen++) {
    // Sort population by fitness
    population.sort((a, b) => b.fitness - a.fitness);

    // A) CULTURAL ACCEPTANCE: Update belief space
    const acceptedCount = Math.max(1, Math.floor(population.length * p));
    const acceptedSet: Chromosome[] = [];
    
    // Top p% by fitness
    acceptedSet.push(...population.slice(0, acceptedCount));
    
    // Top p% by lowest energy (PERFORMANCE FIX: pre-compute energy once per frog, cache during sort)
    // CRITICAL: Calculate energy once per frog instead of repeatedly in sort comparisons
    const energyCache = new Map<Chromosome, number>();
    for (const frog of population) {
      if (!energyCache.has(frog)) {
        energyCache.set(frog, calculateEnergy(frog.x, matrix, taskCount, serviceCount));
      }
    }
    
    const energyIndices = population.map((_, idx) => idx);
    energyIndices.sort((idxA, idxB) => {
      const energyA = energyCache.get(population[idxA])!;
      const energyB = energyCache.get(population[idxB])!;
      return energyA - energyB;
    });
    // Add top p% by energy using indices (avoids copying entire population)
    for (let i = 0; i < acceptedCount && i < energyIndices.length; i++) {
      acceptedSet.push(population[energyIndices[i]]);
    }
    
    // Remove duplicates (simple check by reference, could be improved)
    const uniqueAcceptedSet: Chromosome[] = [];
    const seen = new Set<string>();
    for (const frog of acceptedSet) {
      const key = frog.x.join(',');
      if (!seen.has(key)) {
        seen.add(key);
        uniqueAcceptedSet.push(frog);
      }
    }
    
    updateBeliefSpace(beliefSpace, uniqueAcceptedSet, taskCount, serviceCount, matrix, M);

    // B) SFLA MEMEPLEX EVOLUTION
    let memeplexes = createMemeplexes(population, parameters.memeplexCount);
    const globalBestFrog = findBestChromosome(population);
    
    for (const memeplex of memeplexes) {
      evolveMemeplexWithBelief(
        memeplex,
        globalBestFrog,
        beliefSpace,
        taskCount,
        serviceCount,
        matrix,
        localSteps,
        alpha,
        beliefSpace.beta,
        gamma,
        r,
        rng
      );
    }

    // Shuffle memeplexes
    shuffleMemeplexes(memeplexes, population);
    population.sort((a, b) => b.fitness - a.fitness);

    // C) GA-STYLE DIVERSITY INJECTION
    const children = applyGADiversityInjection(
      population,
      beliefSpace,
      taskCount,
      serviceCount,
      matrix,
      parameters.crossoverRate,
      parameters.mutationRate,
      rng,
      3
    );

    // Evaluate children
    for (const child of children) {
      calculateChromosomeFitness(child, matrix, taskCount, serviceCount);
    }

    // Merge and select (with elitism)
    const eliteCount = Math.max(1, Math.floor(population.length * elitePercent));
    const elites = population.slice(0, eliteCount).map(chrom => ({
      x: [...chrom.x],
      fitness: chrom.fitness,
      rwp: chrom.rwp,
    }));

    // Combine population and children, then sort
    const combined = [...population, ...children];
    combined.sort((a, b) => b.fitness - a.fitness);

    // Select top popSize (preserving elites if better)
    population = combined.slice(0, parameters.populationSize - eliteCount);
    
    // Restore elites (only if they're still best)
    for (let i = 0; i < eliteCount && i < elites.length; i++) {
      if (i < population.length && elites[i].fitness > population[i].fitness) {
        population.splice(i, 0, elites[i]);
      } else if (i >= population.length) {
        population.push(elites[i]);
      }
    }

    population = population.slice(0, parameters.populationSize);
    population.sort((a, b) => b.fitness - a.fitness);

    // D) ADAPTIVE CONTROL
    const currentBest = findBestChromosome(population);
    bestFitnessHistory.push(currentBest.fitness);
    if (bestFitnessHistory.length > S) {
      bestFitnessHistory.shift();
    }

    // Check stagnation
    if (bestFitnessHistory.length >= S) {
      const recentBest = bestFitnessHistory[bestFitnessHistory.length - 1];
      const olderBest = bestFitnessHistory[0];
      
      if (recentBest <= olderBest * 1.01) { // Less than 1% improvement
        stagnationCount++;
        
        // Increase belief influence and mutation
        beliefSpace.beta = Math.min(0.7, beliefSpace.beta * 1.1);
        // Note: mutationRate is handled via parameters, could be adjusted if adaptive
        
        // Reshuffle memeplexes for diversity
        memeplexes = createMemeplexes(population, parameters.memeplexCount);
        shuffleMemeplexes(memeplexes, population);
        population.sort((a, b) => b.fitness - a.fitness);
        memeplexes = createMemeplexes(population, parameters.memeplexCount);
      } else {
        stagnationCount = 0;
      }
    }

    // Check diversity
    const diversity = calculatePopulationDiversity(population, taskCount);
    if (diversity > 0.85) {
      // Low diversity: decrease belief influence, increase random mutation
      beliefSpace.beta = Math.max(0.1, beliefSpace.beta * 0.95);
    }

    // Report progress
    const best = findBestChromosome(population);
    if (onGenerationComplete) {
      onGenerationComplete(gen + 1, best.fitness);
    }

    // Yield control to allow UI updates
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return findBestChromosome(population);
}
